// src/components/SuperAdmin/ProjectDetails.js
import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Backsheet, Button, FormInput } from '../ui/UIComponents';
import { toast } from 'react-hot-toast';

export default function ProjectDetails({ slug, isOpen, onClose, refreshHighlights }) {
    const [details, setDetails] = useState({
        project_rera: '',
        project_landmark: '',
        project_possession_date: '',
        project_land_area: '',
        project_block_no: '',
        project_floor_no: '',
        project_total_units: '',
        project_highlights: '',
        project_overview: '',
        project_brochure: false,
        project_video: '',
        project_amenities: [],
        project_lat: '',
        project_long: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen || !slug) return;
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const snap = await getDoc(doc(db, 'projects', slug));
                if (snap.exists()) {
                    const data = snap.data();
                    setDetails({
                        project_rera: data.project_rera || '',
                        project_landmark: data.project_landmark || '',
                        project_possession_date: data.project_possession_date || '',
                        project_land_area: data.project_land_area || '',
                        project_block_no: data.project_block_no || '',
                        project_floor_no: data.project_floor_no || '',
                        project_total_units: data.project_total_units || '',
                        project_highlights: data.project_highlights || '',
                        project_overview: data.project_overview || '',
                        project_brochure: data.project_brochure || false,
                        project_video: data.project_video || '',
                        project_amenities: Array.isArray(data.project_amenities) ? data.project_amenities : [],
                        project_lat: data.project_lat || '',
                        project_long: data.project_long || ''
                    });
                } else {
                    // reset to defaults
                    setDetails(prev => ({ ...prev }));
                }
            } catch (e) {
                console.error('Failed to load project details', e);
                toast.error('Failed to load details');
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [isOpen, slug]);

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setDetails(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        if (!slug) return;

        // 1) Validate RERA is present
        if (!details.project_rera || !details.project_rera.toString().trim()) {
            toast.error('RERA Number is required');
            return;
        }

        setLoading(true);
        try {
            await setDoc(doc(db, 'projects', slug), details, { merge: true });
            toast.success('Details saved');
            refreshHighlights();
            onClose();
        } catch (e) {
            console.error('Save details failed', e);
            toast.error('Save failed');
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = async () => {
        if (!slug) return;
        if (!window.confirm(`Delete details for '${slug}'?`)) return;
        setLoading(true);
        try {
            await deleteDoc(doc(db, 'projects', slug));
            toast.success('Details deleted');
            refreshHighlights();
            onClose();
        } catch (e) {
            console.error('Delete details failed', e);
            toast.error('Delete failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Backsheet isOpen={isOpen} onClose={onClose} title={`Details: ${slug}`}>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="space-y-4">
                    <FormInput label="RERA Number" name="project_rera" value={details.project_rera} onChange={handleChange} required />
                    <FormInput label="Landmark" name="project_landmark" value={details.project_landmark} onChange={handleChange} />
                    <FormInput label="Possession Date" name="project_possession_date" value={details.project_possession_date} onChange={handleChange} />
                    <FormInput label="Land Area" name="project_land_area" value={details.project_land_area} onChange={handleChange} />
                    <FormInput label="Block No" name="project_block_no" value={details.project_block_no} onChange={handleChange} />
                    <FormInput label="Floor No" name="project_floor_no" value={details.project_floor_no} onChange={handleChange} />
                    <FormInput label="Total Units" name="project_total_units" value={details.project_total_units} onChange={handleChange} />
                    <FormInput label="Highlights HTML" name="project_highlights" value={details.project_highlights} onChange={handleChange} textarea />
                    <FormInput label="Overview HTML" name="project_overview" value={details.project_overview} onChange={handleChange} textarea />
                    <FormInput label="Brochure Available" name="project_brochure" type="checkbox" checked={details.project_brochure} onChange={handleChange} />
                    <FormInput label="Video URL" name="project_video" value={details.project_video} onChange={handleChange} />
                    <FormInput label="Amenities (comma-sep)" name="project_amenities" value={details.project_amenities.join(', ')} onChange={e => setDetails(prev => ({ ...prev, project_amenities: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
                    <FormInput label="Latitude" name="project_lat" value={details.project_lat} onChange={handleChange} />
                    <FormInput label="Longitude" name="project_long" value={details.project_long} onChange={handleChange} />
                    <div className="flex justify-between">
                        <Button variant="danger" onClick={handleDelete} disabled={loading}>Delete</Button>
                        <div className="ml-auto flex space-x-2">
                            <Button variant="secondary" onClick={onClose}>Cancel</Button>
                            <Button onClick={handleSave} disabled={loading}>Save Details</Button>
                        </div>
                    </div>
                </div>
            )}
        </Backsheet>
    );
}
